import { Box, Button, ButtonGroup, Tooltip, Typography } from "@mui/joy";
import { ClickAwayListener } from "@mui/material";
import { useAskQuestion } from "features/context/AskQuestion/AskQuestion";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import {
  selectedPdProjectIDSelector,
  selectedProjectIDSelector,
} from "reducers/rhapsody";
import { paydayApi } from "redux/api/paydayApi";
import { usePdProject } from "redux/pdProject/pdProjectHooks";
import { usePaydaySyncMutation } from "redux/project/projectEndpoints";

/**
 *
 * @returns {ReactElement} PdSettings page
 */
export function PdSettings() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const pdProjectID = useSelector(selectedPdProjectIDSelector);
  const rhapsodyProjectID = useSelector(selectedProjectIDSelector);
  const [doublingScale, setDoublingScale] = useState("");
  const [benefitScale, setBenefitScale] = useState("");
  const [syncPdProject] = usePaydaySyncMutation();
  const { pdProject } = usePdProject(pdProjectID);
  const dispatch = useDispatch();

  useEffect(() => {
    if (pdProject) {
      setDoublingScale(pdProject.doublingScale);
      setBenefitScale(pdProject.benefitScale);
    }
  }, [pdProject]);

  const askQuestion = useAskQuestion();

  const doubligBS = doublingScale === "bs" || doublingScale === "sw";
  const doublingSW = doublingScale === "sw";

  const benefitsBS =
    benefitScale === "bs" || benefitScale === "sw" || benefitScale === "swa";
  const benefitsSW = benefitScale === "sw" || benefitScale === "swa";
  const benefitsA = benefitScale === "swa";

  const reSync = () => {
    askQuestion("Are you sure?", ["Cancel", "Yes"], {
      subtitle: (
        <Typography>Non-locked payroll will be recalculated</Typography>
      ),
    }).then((i) => {
      if (i == 1) {
        setLoading(true);
        syncPdProject({
          id: rhapsodyProjectID,
          body: {
            id: pdProject?.id,
            childID: pdProject?.childID,
            doublingScale,
            benefitScale,
          },
        })
          .unwrap()
          .then(() => {
            dispatch(paydayApi.util.invalidateTags(["pdProjects"]));
            setLoading(false);
            setOpen(false);
          });
      }
    });
  };

  return (
    <Tooltip
      sx={{ zIndex: 1300 }}
      arrow
      open={open}
      color="neutral"
      variant="outlined"
      title={
        <ClickAwayListener
          disableReactTree
          onClickAway={() => {
            setOpen(false);
          }}
        >
          <Box
            sx={{
              width: 500,
              display: "flex",
              flexDirection: "column",
              p: 1,
              gap: 1,
            }}
          >
            <Typography level="h6">Doubling</Typography>
            <ButtonGroup>
              <Button
                onClick={() => setDoublingScale("bs")}
                variant={doubligBS ? "soft" : undefined}
                color={doubligBS ? "primary" : "neutral"}
              >
                Base Scale
              </Button>
              <Button
                onClick={() => setDoublingScale("sw")}
                variant={doublingSW ? "soft" : undefined}
                color={doublingSW ? "primary" : "neutral"}
                sx={{ flexDirection: "column" }}
              >
                Scale Wages
                <Typography level="body4" sx={{ color: "inherit" }}>
                  (Base Scale + Overscale)
                </Typography>
              </Button>
            </ButtonGroup>
            <Typography level="h6">Benefits</Typography>
            <ButtonGroup>
              <Button
                onClick={() => setBenefitScale("bs")}
                variant={benefitsBS ? "soft" : undefined}
                color={benefitsBS ? "primary" : "neutral"}
                sx={{ flexDirection: "column" }}
              >
                Base Scale
              </Button>
              <Button
                onClick={() => setBenefitScale("sw")}
                variant={benefitsSW ? "soft" : undefined}
                color={benefitsSW ? "primary" : "neutral"}
                sx={{ flexDirection: "column" }}
              >
                Scale Wages
                <Typography level="body4" sx={{ color: "inherit" }}>
                  (Base Scale + Overscale)
                </Typography>
              </Button>
              <Button
                onClick={() => setBenefitScale("swa")}
                variant={benefitsA ? "soft" : undefined}
                color={benefitsA ? "primary" : "neutral"}
                sx={{ flexDirection: "column" }}
              >
                Adjustment
                <Typography level="body4" sx={{ color: "inherit" }}>
                  (Scale Wages + Adjustment)
                </Typography>
              </Button>
            </ButtonGroup>
            <Box />
            <Box sx={{ display: "flex", gap: 1, justifyContent: "end" }}>
              <Button
                onClick={() => {
                  setOpen(false);
                }}
                variant="soft"
                color="neutral"
                sx={{ flex: 1 }}
              >
                Cancel
              </Button>
              <Button
                loading={loading}
                onClick={() => {
                  reSync();
                }}
                sx={{ flex: 1 }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </ClickAwayListener>
      }
    >
      <Button
        onClick={() => setOpen(true)}
        startDecorator={<i className="fa-solid fa-gear"></i>}
        variant="outlined"
        size="sm"
      >
        Settings
      </Button>
    </Tooltip>
  );
}
